import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { CircularProgress } from "@material-ui/core";
import { notification } from "antd";
import { Strings } from "../../../../../dataProvider/localize";
import { updateSwmsVisibilityV2 } from "../../../../../actions/v2/JobSwmsTaskActions";
import styles from "./SwmsVisibilityConfirmModal.module.css";

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor" className={styles.infoBannerIcon}>
    <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6-46.8 43.5-78.1 95.4-93 131.1-3.3 7.9-3.3 16.7 0 24.6 14.9 35.7 46.2 87.7 93 131.1 47.1 43.7 111.8 80.6 192.6 80.6s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1 3.3-7.9 3.3-16.7 0-24.6-14.9-35.7-46.2-87.7-93-131.1-47.1-43.7-111.8-80.6-192.6-80.6zM144 256a144 144 0 1 1 288 0 144 144 0 1 1-288 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill="currentColor" className={styles.infoBannerIcon}>
    <path d="M84.8 90.5C75.4 81.1 60.2 81.1 50.9 90.5C41.6 99.9 41.5 115.1 50.9 124.4L562.4 635.9C571.8 645.3 587 645.3 596.3 635.9C605.6 626.5 605.7 611.3 596.3 602L472.2 477.9C544.1 434.6 592.8 365.4 615.1 311.9C618.4 304 618.4 295.2 615.1 287.3C600.2 251.6 568.9 199.6 522.1 156.2C475 112.5 410.3 75.6 329.5 75.6C260.4 75.6 202.5 102.6 157.4 135.9L84.8 90.5zM218.4 196.1C250 173.2 287.5 160 329.6 160C364.6 160 396.3 169.1 424.7 184.3L371.1 237.9C358.8 232.8 345.3 230 331.1 230C273.7 230 227.1 276.6 227.1 334C227.1 348.2 229.9 361.7 235 374L190 419C173.8 390.2 164.7 358.5 164.7 323.5C164.7 273.9 184.3 228.8 218.4 196.1zM501.2 358.4L455.8 313C450.5 370.2 404.9 415.8 347.7 421.1L302.3 466.5C311.2 468.2 320.4 469.1 329.7 469.1C379.3 469.1 424.4 449.5 457.1 414.9C479.9 383.3 493.2 345.9 501.2 358.4z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor" className={styles.closeBtnIcon}>
    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.3c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
  </svg>
);

export default function SwmsVisibilityConfirmModal({ swms, jobId, targetVisible, onClose }) {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const formName = swms && swms.swms_form_name ? swms.swms_form_name : "SWMS";
  const formNumber = swms && swms.swms_number ? swms.swms_number : "-";
  const isEnabling = targetVisible === true;

  const title = isEnabling ? "Show SWMS On Site" : "Hide SWMS From Site";
  const prompt = isEnabling
    ? "Are you sure you want to make this SWMS visible to site team members?"
    : "Are you sure you want to hide this SWMS from site team members?";
  const warning = isEnabling
    ? "Once confirmed, this SWMS will be available to site users."
    : "Once confirmed, this SWMS will no longer be available to site users.";
  const confirmText = isEnabling ? "Yes, Show On Site" : "Yes, Hide From Site";
  const successText = isEnabling
    ? "SWMS visibility is now enabled for site."
    : "SWMS visibility is now disabled for site.";

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget && !submitting) {
      onClose();
    }
  };

  const handleConfirm = () => {
    if (!swms || !swms.swms_form_id || !jobId) {
      notification.error({
        message: Strings.error_title,
        description: Strings.generic_error,
        className: "ant-error"
      });
      onClose();
      return;
    }

    setSubmitting(true);
    dispatch(updateSwmsVisibilityV2(jobId, swms.swms_form_id, isEnabling)).then((message) => {
      setSubmitting(false);
      notification.success({
        message: Strings.success_title,
        description: message || successText,
        className: "ant-success"
      });
      onClose();
    }).catch((err) => {
      setSubmitting(false);
      notification.error({
        message: Strings.error_title,
        description: err || Strings.generic_error,
        className: "ant-error"
      });
    });
  };

  return (
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h3>{title}</h3>
            <p className={styles.headerSubtitle}>{formName} · {formNumber}</p>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            disabled={submitting}
            type="button"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.infoBanner}>
            {isEnabling ? <EyeIcon /> : <EyeSlashIcon />}
            <span>{prompt}</span>
          </div>
          <p className={styles.warningText}>{warning}</p>
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className={styles.confirmBtn} onClick={handleConfirm} disabled={submitting}>
            {submitting ? <CircularProgress size={16} style={{ color: "#fff" }} /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
